import React, { useEffect } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';

import { useAuth } from '../../contexts/AuthContext';

import AccessDeniedMessage from './AccessDeniedMessage';

interface ProtectedRouteProps {
  children: React.ReactNode;
  fallback?: React.ReactNode;
  redirectTo?: string;
  showAccessDeniedMessage?: boolean;
}

/**
 * 認証が必要なルートを保護するコンポーネント
 *
 * 要件 6.3: ユーザーが認証なしで保護された機能にアクセスしようとしたとき、システムはログインページにリダイレクトする
 * 要件 6.4: 認証後の元ページ復帰機能
 */
const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  fallback,
  redirectTo = '/auth/login',
  showAccessDeniedMessage = true,
}) => {
  const { isAuthenticated, isLoading } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  // ログアウト後の状態変化を検知してリダイレクト
  useEffect(() => {
    if (!isLoading && !isAuthenticated && location.pathname.startsWith('/user/')) {
      // ユーザー関連ページでログアウト状態になった場合、TOPページにリダイレクト
      const timer = setTimeout(() => {
        navigate('/', { replace: true });
      }, 10000); // 10秒後にリダイレクト

      return () => clearTimeout(timer);
    }
  }, [isAuthenticated, isLoading, location.pathname, navigate]);

  // 認証状態の確認中はローディング表示
  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen" data-testid="loading-spinner">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
        <span className="ml-4 text-gray-600">認証状態を確認中...</span>
      </div>
    );
  }

  // 未認証の場合
  if (!isAuthenticated) {
    // カスタムフォールバックが指定されている場合はそれを使用
    if (fallback) {
      return <>{fallback}</>;
    }

    // アクセス拒否メッセージを表示する設定が有効な場合
    if (showAccessDeniedMessage) {
      return <AccessDeniedMessage />;
    }

    // デフォルトのリダイレクト処理
    return (
      <Navigate to={redirectTo} state={{ from: location.pathname + location.search }} replace />
    );
  }

  // 認証済みの場合は子コンポーネントを表示
  return <div data-testid="protected-route">{children}</div>;
};

export default ProtectedRoute;
